import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingProvider } from './context/SettingContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { PuffLoader } from 'react-spinners';
import { clearTokens } from './controllers/AuthController';
import Cookies from 'js-cookie';
import NotFound from './views/error/notFound';
import Login from './views/login';
import ChangePassword from './views/changePassword';
import UserRole from './views/userRole';
import EditUserRole from './views/userRole/pages/Edit';
import CreateUserRole from './views/userRole/pages/Create';
import User from './views/user';
import CreateUser from './views/user/pages/Create';
import EditUser from './views/user/pages/Edit';
import Dashboard from './views/dashboard';
import Sufferer from './views/sufferer';
import DetailSufferer from './views/sufferer/pages/Detail';
import EditSufferer from './views/sufferer/pages/Edit';
import PanicLog from './views/panicLog';
import EditPanicLog from './views/panicLog/pages/Edit';
import TherapyMaterial from './views/therapyMaterial';
import DetailTherapyMaterial from './views/therapyMaterial/pages/Detail';
import CreateTherapyMaterial from './views/therapyMaterial/pages/Create';
import EditTherapyMaterial from './views/therapyMaterial/pages/Edit';
import ScreeningQuestion from './views/screeningQuestion';
import EditScreeningQuestion from './views/screeningQuestion/pages/Edit';
import ScreeningLog from './views/screeningLog';
import History from './views/history';
import LogoTitleSetting from './views/logoTitleSetting';

const PrivateRoute = ({ element, requiredRole }) => {
  const { auth, loading } = useAuth();
  
  if (loading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <PuffLoader color='#044E8E' loading={loading} size={50} />
      </div>
    );
  };
  
  const isAuthenticated = !!auth;
  if (!isAuthenticated) {
    clearTokens();
    return <Navigate to='/' replace />;
  };
  
  if (requiredRole && !auth?.role.name.toLowerCase().includes(requiredRole.toLowerCase())) {
    return <Navigate to='/dashboard' replace />;
  };
  
  return element;
};

const useScreenSize = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return isMobile;
};

function App() {
  const isMobile = useScreenSize();
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  
  useEffect(() => {
    const refreshToken = Cookies.get('refresh');
    
    if (refreshToken) {
      setIsLoggedIn(true);
    } else {
      clearTokens();
      setIsLoggedIn(false);
    };
  }, []);
  
  if (isLoggedIn === null) {
    return null;
  };
  
  return (
    <AuthProvider>
      <SettingProvider>
        <Router>
          <Toaster
            position={isMobile ? 'top-center' : 'top-right'}
            containerStyle={{
              top: 80,
              left: 0,
              bottom: 0,
              right: isMobile ? 0 : 20,
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '500',
              zIndex: 1,
            }}
            toastOptions={{
              style: {
                height: '40px',
                borderRadius: '8px',
                color: '#000',
              },
              success: {
                iconTheme: {
                  primary: '#25C23E',
                  secondary: '#FFF',
                },
              },
              error: {
                iconTheme: {
                  primary: '#CC0000',
                  secondary: '#FFF',
                },
              },
              duration: 3000,
            }}
          />
          <Routes>
            <Route path='*' element={<NotFound />} />
            <Route path='/' element={isLoggedIn ? <Navigate to='/dashboard' /> : <Login />} />
            
            {/* User Management */}
            <Route path='/change-password' element={<PrivateRoute element={<ChangePassword />} />} />
            <Route path='/user-role' element={<PrivateRoute element={<UserRole />} requiredRole={'admin'} />} /> 
            <Route path='/user-role/create' element={<PrivateRoute element={<CreateUserRole />} requiredRole={'admin'} />} />
            <Route path='/user-role/:id/edit' element={<PrivateRoute element={<EditUserRole />} requiredRole={'admin'} />} />
            <Route path='/user' element={<PrivateRoute element={<User />} requiredRole={'admin'} />} />
            <Route path='/user/create' element={<PrivateRoute element={<CreateUser />} requiredRole={'admin'} />} />
            <Route path='/user/:id/edit' element={<PrivateRoute element={<EditUser />} requiredRole={'admin'} />} />
            
            {/* Main Menu */}
            <Route path='/dashboard' element={<PrivateRoute element={<Dashboard />} />} />
            <Route path='/sufferer' element={<PrivateRoute element={<Sufferer />} />} />
            <Route path='/sufferer/:id/detail' element={<PrivateRoute element={<DetailSufferer />} />} />
            <Route path='/sufferer/:id/edit' element={<PrivateRoute element={<EditSufferer />} />} />
            <Route path='/panic-log' element={<PrivateRoute element={<PanicLog />} />} />
            <Route path='/panic-log/:id/user/:user_id/edit' element={<PrivateRoute element={<EditPanicLog />} />} />
            <Route path='/therapy-material' element={<PrivateRoute element={<TherapyMaterial />} />} />
            <Route path='/therapy-material/:id/detail' element={<PrivateRoute element={<DetailTherapyMaterial />} />} />
            <Route path='/therapy-material/create' element={<PrivateRoute element={<CreateTherapyMaterial />} />} />
            <Route path='/therapy-material/:id/edit' element={<PrivateRoute element={<EditTherapyMaterial />} />} />
            <Route path='/screening-question' element={<PrivateRoute element={<ScreeningQuestion />} />} />
            <Route path='/screening-question/:id/edit' element={<PrivateRoute element={<EditScreeningQuestion />} />} />
            <Route path='/screening-log' element={<PrivateRoute element={<ScreeningLog />} />} />
            <Route path='/history' element={<PrivateRoute element={<History />} requiredRole={'admin'} />} />
            <Route path='/logo-title-setting' element={<PrivateRoute element={<LogoTitleSetting />} requiredRole={'admin'} />} />
          </Routes>
        </Router>
      </SettingProvider>
    </AuthProvider>
  );
};

export default App;
